/**
 * BindCustomerModal(REQ-20260518002):把 LINE 聯絡人綁到系統客戶。
 * 一個客戶可被多個 LINE 聯絡人綁(N:1)。
 * 清單支援 ↑ / ↓ + Enter 選定(REQ-20260903010)。
 */
import { useEffect, useRef, useState } from 'react';
import { Modal } from '../../components/Modal';
import { useListKeyboardNav } from '../../components/useListKeyboardNav';
import { useBindCustomer, useCustomerSearch } from './lineApi';

interface Props {
  open: boolean;
  contactId: number;
  onClose: () => void;
}

export function BindCustomerModal({ open, contactId, onClose }: Props): JSX.Element {
  const [keyword, setKeyword] = useState('');
  const trimmed = keyword.trim();
  const { data: results = [], isLoading } = useCustomerSearch(trimmed, open);
  const bind = useBindCustomer();

  const onPick = (customerId: number): void => {
    bind.mutate(
      { contactId, customerId },
      {
        onSuccess: () => {
          onClose();
          setKeyword('');
        },
      },
    );
  };

  const rowRefs = useRef<Record<number, HTMLButtonElement | null>>({});
  const nav = useListKeyboardNav(results.length, (i) => {
    const c = results[i];
    if (c && !bind.isPending) onPick(c.id);
  });

  // 高亮跑出可視範圍時把該列捲進來(jsdom 沒有 scrollIntoView,要 optional chaining)
  const activeId = nav.index >= 0 ? results[nav.index]?.id : undefined;
  useEffect(() => {
    if (activeId != null) rowRefs.current[activeId]?.scrollIntoView?.({ block: 'nearest' });
  }, [activeId]);

  return (
    <Modal open={open} onClose={onClose} title="綁定客戶" widthClass="max-w-lg">
      <div className="grid gap-3">
        <label className="grid gap-1 text-sm">
          <span className="text-slate-700">搜尋客戶(簡稱 / 編號 / 統編 / 電話 / Email)</span>
          <input
            autoFocus
            type="text"
            value={keyword}
            onChange={(e) => setKeyword(e.target.value)}
            onKeyDown={(e) => {
              nav.onKeyDown(e);
            }}
            placeholder="輸入關鍵字"
            className="border rounded px-2 py-1.5 focus:outline-none focus:ring-2 focus:ring-blue-400"
          />
        </label>

        <div className="border rounded max-h-80 overflow-y-auto">
          {trimmed.length === 0 ? (
            <div className="text-sm text-slate-500 p-3">輸入關鍵字開始搜尋</div>
          ) : isLoading ? (
            <div className="text-sm text-slate-500 p-3">搜尋中…</div>
          ) : results.length === 0 ? (
            <div className="text-sm text-slate-500 p-3">沒有符合的客戶</div>
          ) : (
            results.map((c, i) => (
              <button
                key={c.id}
                ref={(el) => {
                  rowRefs.current[c.id] = el;
                }}
                type="button"
                disabled={bind.isPending}
                onClick={() => onPick(c.id)}
                // hover 變體會蓋掉高亮底色,所以兩者擇一輸出而不是並存
                className={`block w-full text-left px-3 py-2 border-b last:border-b-0 disabled:opacity-50 ${
                  i === nav.index ? 'bg-blue-100' : 'hover:bg-blue-50'
                }`}
                aria-selected={i === nav.index ? true : undefined}
                data-testid={i === nav.index ? 'kbd-active' : undefined}
              >
                <div className="text-sm font-medium text-slate-900">
                  {c.simpleName}
                  {c.cCode ? <span className="text-slate-400 ml-2">#{c.cCode}</span> : null}
                </div>
                <div className="text-xs text-slate-500">{c.fullName}</div>
              </button>
            ))
          )}
        </div>

        {bind.isError ? (
          <div role="alert" className="text-red-600 text-sm">
            綁定失敗:{bind.error instanceof Error ? bind.error.message : '未知錯誤'}
          </div>
        ) : null}

        <div className="flex justify-end">
          <button
            type="button"
            onClick={onClose}
            className="text-sm border rounded px-3 py-1.5 text-slate-700 hover:bg-slate-100"
          >
            取消
          </button>
        </div>
      </div>
    </Modal>
  );
}
